import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import type { AutocompleteChangeEvent, AutocompleteOption } from '@ntv360/component-pantry';
import { Accordion, Autocomplete } from '@ntv360/component-pantry';
import { TerritoryMapService } from '@shared/components/territory-map/services';
import { LOCATOR_DATA } from '../../constants/locator.constants';
import { LocatorService } from '../../services/locator.service';

@Component({
    selector: 'app-territory-list',
    imports: [Accordion, DecimalPipe, DatePipe, Autocomplete, FormsModule],
    templateUrl: './territory-list.component.html',
    styleUrl: './territory-list.component.scss',
})
export class TerritoryListComponent {
    protected territories = LOCATOR_DATA;
    protected console = console;

    private locatorService = inject(LocatorService);
    private territoryMapService = inject(TerritoryMapService);

    /** Tracks which territory index is in edit mode */
    protected editingIndex = signal<number | null>(null);

    /** Editable territory name while in edit mode — plain property for ngModel two-way binding */
    protected editNameValue = '';

    /** Editable dealer id while in edit mode */
    protected editDealer = signal<string>('');

    /** Dealer options for the autocomplete — derived from all dealers across territories */
    protected dealerOptions: AutocompleteOption[] = LOCATOR_DATA.flatMap((t) =>
        t.dealers.map((d) => ({ label: d.dealerName, value: d.dealerId })),
    ).filter((d, idx, arr) => arr.findIndex((x) => x.value === d.value) === idx);

    protected startEdit(tIdx: number, territory: (typeof LOCATOR_DATA)[0], event: Event): void {
        event.stopPropagation();
        this.editingIndex.set(tIdx);
        this.editNameValue = territory.territoryName;
        this.editDealer.set(territory.assignedDealer);
    }

    protected cancelEdit(event: Event): void {
        event.stopPropagation();
        this.editingIndex.set(null);
    }

    protected saveEdit(territory: (typeof LOCATOR_DATA)[0], event: Event): void {
        event.stopPropagation();
        // TODO: wire up real save/API call
        territory.territoryName = this.editNameValue;
        territory.assignedDealer = this.editDealer();
        this.editingIndex.set(null);
    }

    protected onDealerChange(event: AutocompleteChangeEvent): void {
        const option = event.option;
        if (option && !Array.isArray(option)) {
            this.editDealer.set(String(option.value));
        }
    }

    protected onAccordionToggle(territory: (typeof LOCATOR_DATA)[0], isOpen: boolean): void {
        if (!isOpen) return;

        const bounds = (territory.latitude as number[]).map(
            (lat, i) => [lat, (territory.longitude as number[])[i]] as [number, number],
        );
        this.territoryMapService.flyToBounds.set(bounds);
    }

    protected openDeleteModal(territoryName: string, event: Event): void {
        event.stopPropagation();
        this.locatorService.openDeleteModal(territoryName);
    }

    /**
     * Tracks which host cards are expanded. Key: `${territoryIndex}-${hostIndex}`
     */
    protected expandedHosts = new Set<string>();

    protected toggleHost(key: string, event: Event): void {
        event.stopPropagation();
        if (this.expandedHosts.has(key)) {
            this.expandedHosts.delete(key);
        } else {
            this.expandedHosts.add(key);
        }
    }

    protected isHostExpanded(key: string): boolean {
        return this.expandedHosts.has(key);
    }

    protected getOperationHours(
        operationHours: Record<string, { open: string; close: string }>,
    ): { day: string; open: string; close: string }[] {
        return Object.entries(operationHours).map(([day, hours]) => ({
            day: day.charAt(0).toUpperCase() + day.slice(1),
            open: hours.open,
            close: hours.close,
        }));
    }
}
